import { z } from 'zod';
import { claveCatalogo } from './catalogos.js';
import { ErrorDominio } from './errores.js';
/**
 * Second touch: la recepcion de la tanda, el padron de equipos, la remesa de
 * carga y el embarque al sitio.
 *
 * Las formas salen de la migracion 0021 y se escriben UNA vez aqui, por el
 * mismo motivo que las del movil: la app del telefono, la consola y la API
 * tienen que coincidir en los limites. Cuando no coinciden, el telefono
 * acepta una serie de 120 caracteres que el servidor rechaza, y quien captura
 * esta parado frente a una tarima de cajas abiertas.
 *
 * EL EJE, repetido aqui porque es lo primero que se pierde al escribir un
 * formulario: EL EQUIPO NACE VIRGEN Y SIN SITIO. `sitio_id`, `area_destino` y
 * `nomenclatura` son opcionales en el alta y NO se exigen por ninguna via. El
 * destino llega despues, por `equipoDestino`.
 */
/* --------------------------------- Enums --------------------------------- */
/** Donde se hace el trabajo. Misma decision vista en la recepcion, el equipo
 *  y la remesa, y por eso un solo enum para las tres. */
export const MODALIDADES_STAGING = ['STG_FACILIDAD', 'STG_SITIO'];
export const ESTADOS_RECEPCION = ['REC_EN_CONTEO', 'REC_CUADRADA', 'REC_CON_DIFERENCIA'];
export const ESTADOS_EQUIPO = [
    'EQP_VIRGEN',
    'EQP_EN_PROCESO',
    'EQP_LISTO',
    'EQP_EMBARCADO',
    'EQP_INSTALADO',
    'EQP_ERROR',
    'EQP_RETIRADO',
];
export const ESTADOS_REMESA = [
    'REM_ABIERTA',
    'REM_EN_CARGA',
    'REM_VALIDADA',
    'REM_CERRADA',
];
export const RESULTADOS_REMESA = ['RMR_PENDIENTE', 'RMR_CARGADO', 'RMR_ERROR'];
export const TIPOS_DESTINO_GUIA = ['GDE_ALMACEN', 'GDE_SITIO'];
/* -------------------------------- Limites -------------------------------- */
/** Cuantos equipos entran en un alta. Una tarima son cuarenta y ocho cajas y
 *  un embarque grande son dos mil; 500 acota el cuerpo de la peticion sin
 *  obligar a partir una tarima en dos capturas. */
export const MAX_EQUIPOS_POR_ALTA = 500;
/** Cuantas series acepta un paso de avance por lote. */
export const MAX_SERIES_POR_LOTE = 500;
/** La evidencia de la remesa admite lo mismo que la del ticket: el CHECK de
 *  `remesa_evidencias.bytes` es el mismo de `ticket_evidencias`. */
export const MAX_BYTES_EVIDENCIA_REMESA = 10 * 1024 * 1024;
/* --------------------------- Piezas compartidas -------------------------- */
/**
 * La serie, tal como la acepta el CHECK de `equipos.serie`: de tres a ochenta
 * caracteres y sin formato impuesto. No se le pone un patron a proposito —la
 * 0021 lo explica—: cada fabricante usa el suyo y un regex de mas rechaza
 * equipo que si existe.
 *
 * Se normaliza a mayusculas porque el indice unico por cliente es sobre
 * `upper(serie)`: sin normalizar, `ab-1` y `AB-1` pasan la validacion, chocan
 * en la base y el error sale como violacion de unicidad en vez de como
 * "serie repetida".
 */
export const serieEquipo = z.string().trim().toUpperCase().min(3).max(80);
/** El nombre del equipo en el dominio. Mismo patron que el CHECK de la 0021. */
export const nomenclaturaEquipo = z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9._-]{3,60}$/, 'La nomenclatura usa letras, numeros, punto, guion y guion bajo');
const nota = z.string().trim().max(2000);
/* ------------------------------ Recepciones ------------------------------ */
/**
 * El acta de entrada de la tanda.
 *
 * `cantidad_contada` es OPCIONAL y no arranca en cero: nula significa "se
 * esta contando" y cero significa "se conto y no habia nada", que no es lo
 * mismo. El estado NO viaja en el alta: lo DERIVA el servidor de las dos
 * cantidades, porque un estado que manda el cliente es la forma de dejar una
 * recepcion marcada cuadrada con tres piezas de menos, y con eso se pierde el
 * reclamo al proveedor sin tocar un solo numero.
 */
export const recepcionAlta = z
    .object({
    cliente_id: z.string().uuid(),
    proyecto_id: z.string().uuid().optional(),
    /* La facilidad donde se recibio. Obligatoria en modalidad facilidad y
       ausente a sitio, igual que el CHECK `recepcion_facilidad_coherente`. */
    almacen_id: z.string().uuid().optional(),
    modalidad: z.enum(MODALIDADES_STAGING).optional(),
    recibido_por: z.string().trim().min(3).max(120),
    pallets: z.number().int().min(1).max(999).optional(),
    cantidad_declarada: z.number().int().positive().max(100_000),
    cantidad_contada: z.number().int().min(0).max(100_000).optional(),
    nota: nota.optional(),
})
    .refine((r) => (r.modalidad ?? 'STG_FACILIDAD') !== 'STG_FACILIDAD' || r.almacen_id !== undefined, {
    message: 'En modalidad facilidad hay que decir en que facilidad se recibio',
    path: ['almacen_id'],
});
/**
 * El estado que le corresponde a un conteo. Es la unica forma de ponerlo, de
 * este lado y del otro: la API lo llama al insertar y la pantalla lo llama
 * para anticipar lo que va a quedar asentado.
 */
export function estadoDeRecepcion(declarada, contada) {
    if (contada === null)
        return 'REC_EN_CONTEO';
    return contada === declarada ? 'REC_CUADRADA' : 'REC_CON_DIFERENCIA';
}
/* ------------------------------- El padron ------------------------------- */
/**
 * Un equipo del alta. Lo unico obligatorio es la SERIE: es la identidad
 * fisica de la maquina y lo unico que se puede leer de la caja cerrada.
 *
 * Las tres columnas del destino van aqui como opcionales y no se exigen
 * nunca. Estan admitidas porque hay tandas que si llegan destinadas de
 * fabrica, y omitirlas obligaria a dar de alta y luego parchar quinientas
 * veces; lo que no existe es un camino que las pida.
 */
export const equipoAlta = z.object({
    serie: serieEquipo,
    marca: z.string().trim().min(2).max(60).optional(),
    modelo: z.string().trim().min(1).max(80).optional(),
    tipo_equipo: z.string().trim().min(2).max(60).optional(),
    /* Opcional. Ver el encabezado de este archivo. */
    sitio_id: z.string().uuid().optional(),
    area_destino: z.string().trim().min(1).max(120).optional(),
    nomenclatura: nomenclaturaEquipo.optional(),
    nota: nota.optional(),
});
/**
 * El alta del padron: serie por serie o en lote, con la misma ruta.
 *
 * Los datos de pertenencia —cliente, proyecto, contrato, facilidad— son del
 * LOTE y no de cada equipo: una tarima llega de un embarque, para una cuenta
 * y a una facilidad. Repetirlos por renglon invita a que la captura 300 de
 * una tanda de 500 quede con otro proyecto y nadie lo note.
 */
export const padronAlta = z.object({
    proyecto_id: z.string().uuid().optional(),
    contrato_id: z.string().uuid().optional(),
    almacen_id: z.string().uuid().optional(),
    modalidad: z.enum(MODALIDADES_STAGING).optional(),
    equipos: z.array(equipoAlta).min(1).max(MAX_EQUIPOS_POR_ALTA),
});
/**
 * El momento en que el equipo gana su destino.
 *
 * Al menos uno de los tres campos: un PATCH vacio no es una asignacion, y
 * aceptarlo deja una entrada de auditoria que dice que alguien destino un
 * equipo sin haberlo destinado.
 */
export const equipoDestino = z
    .object({
    sitio_id: z.string().uuid().optional(),
    area_destino: z.string().trim().min(1).max(120).optional(),
    nomenclatura: nomenclaturaEquipo.optional(),
    nota: nota.optional(),
})
    .refine((d) => d.sitio_id !== undefined || d.area_destino !== undefined || d.nomenclatura !== undefined, { message: 'Hay que asignar al menos el sitio, el area destino o la nomenclatura' });
/** Los filtros del padron. `sin_sitio` es el tablero del second touch: lo que
 *  falta por destinar antes de poder embarcar nada. */
export const consultaEquipos = z.object({
    cliente: z.string().uuid().optional(),
    proyecto: z.string().uuid().optional(),
    remesa: z.string().trim().max(32).optional(),
    estado: z.enum(ESTADOS_EQUIPO).optional(),
    sin_sitio: z.enum(['0', '1']).optional(),
    serie: z.string().trim().max(80).optional(),
    limite: z.coerce.number().int().min(1).max(500).optional(),
});
/* -------------------------------- Remesas -------------------------------- */
/**
 * La tanda de carga. `imagen_so_id` es opcional en el alta porque la remesa
 * se arma antes de saber con que imagen se va a cargar, y el CHECK
 * `remesa_imagen_al_cargar` es el que no deja arrancar la carga sin ella.
 */
export const remesaAlta = z.object({
    cliente_id: z.string().uuid(),
    proyecto_id: z.string().uuid().optional(),
    almacen_id: z.string().uuid().optional(),
    modalidad: z.enum(MODALIDADES_STAGING).optional(),
    imagen_so_id: z.string().uuid().optional(),
    idc_id: z.string().uuid().optional(),
    nota: nota.optional(),
});
/** Montar equipos en la remesa, por serie. */
export const remesaMontaje = z.object({
    series: z.array(serieEquipo).min(1).max(MAX_SERIES_POR_LOTE),
});
/**
 * Los tres pasos del avance por lote, en el orden del piso.
 *
 *   STG_CARGA              se confirma que la imagen quedo cargada. El equipo
 *                          pasa a EQP_LISTO y su renglon a RMR_CARGADO.
 *   STG_ETIQUETA           se le pego la etiqueta. Es la ultima estacion de
 *                          la mesa y lo que se revisa antes de encajonar.
 *   STG_INVENTARIO_SALIDA  el conteo contra la remesa antes de embarcar.
 */
export const PASOS_LOTE = ['STG_CARGA', 'STG_ETIQUETA', 'STG_INVENTARIO_SALIDA'];
export const loteAvance = z.object({
    paso: z.enum(PASOS_LOTE),
    series: z.array(serieEquipo).min(1).max(MAX_SERIES_POR_LOTE),
    nota: nota.optional(),
});
/**
 * El veredicto de UNA serie dentro del lote.
 *
 * Son cinco valores y no un booleano porque quien escanea necesita saber QUE
 * hacer con la caja que tiene en la mano, y las cuatro formas de fallar piden
 * cosas distintas: la repetida se ignora, la ajena se lleva a otra remesa, la
 * de estado no permitido se revisa en la mesa y la desconocida se da de alta
 * o se devuelve al proveedor.
 */
export const VEREDICTOS_LOTE = [
    'ACEPTADA',
    /** Ya tenia ese avance. Es exito: el telefono reenvia su lista sin saber si
     *  la primera peticion llego. */
    'REPETIDA',
    /** Existe en el padron, pero no esta montada en esta remesa. */
    'AJENA',
    /** Esta en la remesa y su estado no admite el paso. */
    'ESTADO_NO_PERMITIDO',
    /** No existe en el padron del cliente. */
    'DESCONOCIDA',
];
/**
 * El resultado de UN equipo que fallo.
 *
 * La nota es obligatoria y con minimo: el renglon en error es lo que se
 * reclama al proveedor, y "error" sin decir cual no sostiene una garantia.
 */
export const errorEquipo = z.object({
    nota: z.string().trim().min(5).max(2000),
});
/**
 * Evidencia de la remesa o de un equipo. `serie` nula es evidencia de LA
 * REMESA COMPLETA —la foto de las veinte maquinas en la mesa—; con serie, es
 * la de ese equipo, y la llave compuesta de la 0021 exige que ese equipo este
 * en esa remesa.
 */
export const evidenciaRemesa = z.object({
    nombre: z.string().trim().min(1).max(120),
    contenido_base64: z.string().min(1),
    /* El Content-Type que declara el cliente. No decide el tipo —eso lo decide
       el contenido— pero queda en la bitacora cuando no corresponde. */
    mime: z.string().trim().max(120).optional(),
    serie: serieEquipo.optional(),
    a_nombre_de: z.string().trim().max(120).optional(),
});
export const cierreRemesa = z.object({
    nota: nota.optional(),
});
/* -------------------------------- Embarque ------------------------------- */
/**
 * La guia que lleva equipos preparados a la sucursal del cliente.
 *
 * No lleva `destino_sitio_id`: el sitio se DEDUCE de los equipos, y que todos
 * coincidan es una regla del embarque (ETK-STG-007). Si el sitio viniera en
 * el cuerpo, una guia podria declarar un destino distinto del de las maquinas
 * que mete, y la caja llegaria a una sucursal mientras el padron dice otra.
 */
export const guiaSitioAlta = z.object({
    /* Opcional y solo para desempatar: la serie es unica POR CLIENTE, asi que
       dos cuentas pueden traer la misma y el embarque tiene que saber de cual
       habla. Sin ambiguedad no hace falta mandarlo. */
    cliente_id: z.string().uuid().optional(),
    numero_guia: z.string().trim().min(4).max(60),
    paqueteria_id: z.string().uuid(),
    almacen_origen_id: z.string().uuid(),
    series: z.array(serieEquipo).min(1).max(MAX_SERIES_POR_LOTE),
    tickets: z.array(z.string().uuid()).max(50).optional(),
    nota: nota.optional(),
});
export const despachoGuiaSitio = z.object({
    responsable: z.string().trim().min(3).max(120).optional(),
});
/* -------------------------------- Catalogos ------------------------------ */
/**
 * La imagen de sistema, por cliente. `id_imagen` es el identificador del lado
 * del CLIENTE —el que aparece en su reporte de cumplimiento— y se guarda tal
 * como el lo escribe: no se normaliza, porque hay que poder citarlo textual.
 */
export const imagenSoAlta = z.object({
    cliente_id: z.string().uuid(),
    clave: claveCatalogo('iso'),
    nombre: z.string().trim().min(2).max(160),
    sistema_operativo: z.string().trim().min(2).max(80),
    version: z.string().trim().min(1).max(40),
    id_imagen: z.string().trim().min(1).max(80),
    /* Para dimensionar la carga: una imagen de 60 GB por USB son horas por
       equipo y cambia la decision de hacerlo en facilidad o en sitio. */
    peso_gb: z.number().positive().max(9999.99).optional(),
    activo: z.boolean().optional(),
});
/** Los cinco marcadores que el CHECK de `plantillas_etiqueta.zpl` exige. */
export const MARCADORES_ETIQUETA = [
    '{marca}',
    '{modelo}',
    '{serie}',
    '{imagen}',
    '{area}',
];
/**
 * La plantilla ZPL. `cliente_id` es OPCIONAL y nulo significa la plantilla
 * generica de Enteratek, valida para cualquier cuenta.
 *
 * Los cinco marcadores se validan de este lado tambien: el CHECK de la base
 * los exige, pero el error que levanta no dice cual falta, y una plantilla sin
 * `{serie}` imprime cien etiquetas identicas —lo que no se nota hasta que hay
 * cien cajas rotuladas igual—.
 */
export const plantillaEtiquetaAlta = z
    .object({
    cliente_id: z.string().uuid().optional(),
    clave: claveCatalogo('etq'),
    nombre: z.string().trim().min(2).max(160),
    ancho_mm: z.number().positive().max(500),
    alto_mm: z.number().positive().max(500),
    dpi: z.union([z.literal(152), z.literal(203), z.literal(300), z.literal(600)]).optional(),
    zpl: z.string().min(1).max(20_000),
    activo: z.boolean().optional(),
})
    .superRefine((p, ctx) => {
    for (const marcador of MARCADORES_ETIQUETA) {
        if (!p.zpl.includes(marcador)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['zpl'],
                message: `La plantilla necesita el marcador ${marcador}`,
            });
        }
    }
});
/* =========================================================================
   Lo que la WEB del second touch necesita y la API del movil no usaba.
   
   Viene de la rama `second-touch` (PR #14), adaptado a los nombres de este
   esquema: la facilidad es `almacen`, y los marcadores de etiqueta llevan
   llaves. Lo que ya existia arriba NO se duplico: gana lo desplegado, que es
   lo que hoy opera y lo que el movil consume.
   ========================================================================= */
/** Las ocho actividades, en el orden en que las definio la operacion. */
export const ACTIVIDADES_STAGING = [
    { orden: 1, nombre: 'Recibir equipo paletizado', minutos: 30 },
    { orden: 2, nombre: 'Inventario de equipos virgenes con serie, modelo y marca', minutos: 20 },
    { orden: 3, nombre: 'Determinar la imagen del proyecto y su manual', minutos: 15 },
    { orden: 4, nombre: 'Cargar la imagen en remesas y validar con evidencia', minutos: 45 },
    { orden: 5, nombre: 'Etiqueta de activo fijo', minutos: 5 },
    { orden: 6, nombre: 'Inventario de los equipos ya cargados', minutos: 15 },
    { orden: 7, nombre: 'Relacion de guias de mensajeria por sitio', minutos: 20 },
    { orden: 8, nombre: 'Trazabilidad de las guias hasta la entrega', minutos: 10 },
];
/**
 * Documentos del proyecto.
 *
 * Existe porque el manual de la imagen de sistema necesitaba donde vivir: la
 * especificacion lo daba por hecho y la biblioteca no estaba construida. Sirve
 * para cualquier documento del proyecto, no solo para el manual.
 */
export const CLASES_DOCUMENTO = [
    'DOC_MANUAL',
    'DOC_PLANO',
    'DOC_PROCEDIMIENTO',
    'DOC_CONTRATO',
    'DOC_ACTA',
    'DOC_OTRO',
];
/**
 * La clave del servicio en el catalogo. Es lo que decide que flujo le toca a
 * un ticket, asi que vive aqui y no escrita a mano en cada consulta.
 */
export const CLAVE_SERVICIO_STAGING = 'ser_second_touch';
export const RESULTADOS_CARGA = ['CAR_PENDIENTE', 'CAR_OK', 'CAR_ERROR'];
export const TIPOS_EQUIPO = ['AIO', 'Desktop', 'Mini', 'Laptop'];
/** Asignacion de sitio y area a varios equipos de un golpe, desde el padron. */
export const asignacionSitio = z.object({
    equipos: z.array(z.string().uuid()).min(1).max(1000),
    sitio_id: z.string().uuid(),
    area_destino: z.string().trim().min(2).max(80).optional(),
});
export const documentoAlta = z.object({
    proyecto_id: z.string().uuid(),
    clase: z.enum(CLASES_DOCUMENTO).default('DOC_OTRO'),
    titulo: z.string().trim().min(2).max(160),
    nombre: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9 _.-]{1,120}$/),
    mime: z.string().trim().min(3).max(120).optional(),
    nota: z.string().trim().max(2000).optional(),
    /* El archivo llega en base64 dentro del JSON, igual que la evidencia del
       servicio: un solo camino de entrada es un solo lugar que auditar. */
    contenido_base64: z.string().min(4),
});
export const documentoEdicion = z
    .object({
    clase: z.enum(CLASES_DOCUMENTO),
    titulo: z.string().trim().min(2).max(160),
    nota: z.string().trim().max(2000).nullable(),
})
    .partial();
/**
 * Embarque a sitios. UNA GUIA POR SITIO: lo que viaja junto llega junto, y
 * una guia por equipo multiplica por 500 el trabajo de la paqueteria.
 */
export const embarqueAlta = z.object({
    proyecto_id: z.string().uuid(),
    paqueteria_id: z.string().uuid(),
    /* El numero de guia lo da la paqueteria, uno por sitio. */
    guias: z
        .array(z.object({
        sitio_id: z.string().uuid(),
        numero_guia: z.string().trim().min(4).max(60),
        equipos: z.array(z.string().uuid()).min(1).max(500),
    }))
        .min(1)
        .max(200),
});
/**
 * Lo que se le edita a un equipo del padron. La serie NO esta: es su
 * identidad, y una identidad que se edita deja de identificar. El estado
 * tampoco: lo mueven la remesa y el embarque, no la captura.
 */
export const equipoEdicion = z
    .object({
    sitio_id: z.string().uuid().nullable(),
    area_destino: z.string().trim().min(2).max(80).nullable(),
    nomenclatura: z.string().trim().min(2).max(80).nullable(),
    almacen_id: z.string().uuid().nullable(),
    nota: z.string().trim().max(2000).nullable(),
    activo: z.boolean(),
})
    .partial();
/** Generacion de la etiqueta de un equipo. */
export const etiquetaSolicitud = z.object({
    plantilla_id: z.string().uuid().optional(),
});
export function interpretarZpl(zpl) {
    const elementos = [];
    let ancho = 400;
    let alto = 200;
    let fuente = 20;
    let altoBarras = 40;
    let x = 0;
    let y = 0;
    let ignorados = 0;
    /* El ^FD que sigue a un comando ^B... es el contenido del codigo de barras,
       no un texto. Es como lo decide la impresora: el ultimo campo abierto
       manda, y ^FS lo cierra. */
    let barrasAbierto = false;
    /* El ZPL es una ristra de comandos que empiezan con ^ o ~, con o sin
       saltos de linea: se parte por el signo y no por renglon, porque la
       plantilla real viene en una sola linea tan seguido como en varias. */
    for (const crudo of zpl.split(/[\^~]/)) {
        const c = crudo.trim();
        if (c === '')
            continue;
        const pw = /^PW(\d+)/.exec(c);
        if (pw?.[1]) {
            ancho = Number(pw[1]);
            continue;
        }
        const ll = /^LL(\d+)/.exec(c);
        if (ll?.[1]) {
            alto = Number(ll[1]);
            continue;
        }
        const cf = /^CF\w?,?(\d+)?/.exec(c);
        if (cf) {
            if (cf[1])
                fuente = Number(cf[1]);
            continue;
        }
        const fo = /^FO(\d+),(\d+)/.exec(c);
        if (fo?.[1] && fo[2]) {
            x = Number(fo[1]);
            y = Number(fo[2]);
            continue;
        }
        const by = /^BY\d*,?[\d.]*,?(\d+)?/.exec(c);
        if (by) {
            if (by[1])
                altoBarras = Number(by[1]);
            continue;
        }
        /* ^BCN,46,Y,N,N — el segundo parametro es el alto del codigo. */
        const bc = /^B[CEQ3]\w?,(\d+)?/.exec(c);
        if (bc) {
            if (bc[1])
                altoBarras = Number(bc[1]);
            barrasAbierto = true;
            continue;
        }
        const fd = /^FD([\s\S]*)$/.exec(c);
        if (fd !== null) {
            elementos.push({
                tipo: barrasAbierto ? 'barras' : 'texto',
                x,
                y,
                alto: barrasAbierto ? altoBarras : fuente,
                valor: (fd[1] ?? '').replace(/FS$/, '').trim(),
            });
            barrasAbierto = false;
            continue;
        }
        if (c === 'XA' || c === 'XZ' || c.startsWith('FS'))
            continue;
        ignorados += 1;
    }
    return { ancho, alto, elementos, ignorados };
}
export const plantillaEtiquetaEdicion = plantillaEtiquetaAlta
    .innerType()
    .partial()
    .omit({ clave: true });
export const recepcionEdicion = z
    .object({
    cantidad_contada: z.number().int().min(0).max(20000),
    nota: z.string().trim().max(2000),
})
    .partial();
export const remesaEdicion = z
    .object({
    idc_id: z.string().uuid().nullable(),
    nota: z.string().trim().max(2000).nullable(),
})
    .partial();
/** Mete equipos virgenes del proyecto a la remesa. */
export const remesaEquipos = z.object({
    equipos: z.array(z.string().uuid()).min(1).max(1000),
});
/**
 * El resultado de la carga, por lote o por renglon.
 *
 * `CAR_ERROR` exige nota porque es lo que se le reclama al proveedor: un
 * equipo marcado con error y sin decir por que no sirve para reclamar nada.
 * La base lo exige igual, en `renglon_error_con_nota`.
 */
export const resultadoCarga = z
    .object({
    equipos: z.array(z.string().uuid()).min(1).max(1000),
    resultado: z.enum(['CAR_OK', 'CAR_ERROR']),
    nota: z.string().trim().max(2000).optional(),
})
    .refine((d) => d.resultado !== 'CAR_ERROR' || (d.nota !== undefined && d.nota.length > 0), {
    message: 'Un equipo con error de carga exige la nota de por que fallo',
    path: ['nota'],
});
/** Una serie capturada en la recepcion. De aqui nace un equipo del padron. */
export const serieRecibida = z.object({
    serie: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9._/-]{3,60}$/),
    marca: z.string().trim().min(2).max(60),
    modelo: z.string().trim().min(1).max(80),
    tipo_equipo: z.enum(TIPOS_EQUIPO),
});
/**
 * Sustituye los marcadores de una plantilla con los datos de un equipo.
 *
 * Un dato ausente deja el marcador a la vista en vez de dejar un hueco: una
 * etiqueta que dice "{area}" se nota y se corrige; una que dice "Area:" y nada
 * se imprime, se pega y nadie la mira otra vez.
 */
export function sustituirMarcadores(zpl, datos) {
    let salida = zpl;
    for (const marcador of MARCADORES_ETIQUETA) {
        const campo = marcador.slice(1, -1);
        const valor = datos[campo];
        if (valor === undefined || valor === '')
            continue;
        /* Sin expresion regular: el valor viene de la captura y un `$&` o un
           `$1` adentro se expandiria contra el texto de la plantilla. */
        salida = salida.split(marcador).join(valor);
    }
    return salida;
}
/**
 * ETK-STG-007 y ETK-STG-008. Las mismas dos reglas que hace cumplir la base
 * en `plantillas_etiqueta`; aqui estan para que el formulario las diga antes
 * de guardar, no para reemplazarlas.
 */
export function validarZpl(zpl) {
    if (!/^\s*\^XA/.test(zpl) || !/\^XZ\s*$/.test(zpl)) {
        throw new ErrorDominio('ETK-STG-007');
    }
    if (!zpl.includes('{serie}')) {
        throw new ErrorDominio('ETK-STG-008');
    }
}
//# sourceMappingURL=staging.js.map